<div class="container-xxl py-10">
  <!-- Page Header -->
  <div class="d-flex flex-wrap flex-stack mb-10">
    <h1 class="fw-bold my-2">{{ 'organisations.title' | translate }}</h1>
  </div>

  <!-- Filters Section -->
  <div class="card mb-10">
    <div class="card-body">
      <div class="row g-5 align-items-end">
        <!-- Search Filter -->
        <div class="col-md-5">
          <app-input-component
            [type]="'text'"
            [(value)]="searchQuery"
            [label]="'organisations.search' | translate"
            [placeholder]="'organisations.searchPlaceholder' | translate"
            [floating]="true"
            (valueChange)="onSearchChange()"
          />
        </div>

        <!-- Type Filter -->
        <div class="col-md-3">
          <app-selectorini
            [options]="companyTypes"
            [displayKey]="'label'"
            [valueKey]="'value'"
            [(value)]="selectedType"
            [label]="'organisations.type' | translate"
            [placeholder]="'organisations.allTypes' | translate"
            [floating]="true"
            (valueChange)="onTypeChange()"
          />
        </div>

        <!-- Sort by Nearest Button -->
        <div class="col-md-2">
          <button
            class="btn w-100"
            [class.btn-primary]="sortByDistance"
            [class.btn-light]="!sortByDistance"
            (click)="toggleSortByDistance()"
            [disabled]="!userLocation"
            style="height: 50px;"
            [title]="userLocation ? ('competitions.sortByNearest' | translate) : ('competitions.waitingForLocation' | translate)"
          >
            @if (locationLoading) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            } @else {
              <i class="fa fa-location-crosshairs me-2"></i>
            }
            {{ 'competitions.nearest' | translate }}
          </button>
        </div>

        <!-- Clear Filters Button -->
        <div class="col-md-2">
          <button
            class="btn btn-light w-100"
            (click)="clearFilters()"
            style="height: 50px;"
          >
            <i class="fa fa-times me-2"></i>
            {{ 'competitions.clear' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Map Section -->
  @if (!loading && filteredCompanies.length > 0) {
    <div class="mb-10">
      <app-location-map
        [markers]="mapMarkers"
        [config]="mapConfig"
        [loading]="geocodingInProgress"
        (markerClick)="onMapMarkerClick($event)"
        (infoWindowAction)="onMapInfoWindowAction($event)"
      />
      <!-- Map Legend -->
      <div class="d-flex flex-wrap gap-4 mt-4 justify-content-center">
        @for (type of companyTypes; track type.value) {
          @if (type.value) {
            <div class="d-flex align-items-center">
              <span class="legend-dot" [style.background-color]="type.color === 'orange' ? '#fb8c00' : type.color === 'blue' ? '#1e88e5' : type.color === 'purple' ? '#8e24aa' : '#e53935'"></span>
              <span class="text-muted fs-7">{{ type.label }}</span>
            </div>
          }
        }
      </div>
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="d-flex flex-column align-items-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'organisations.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'organisations.loading' | translate }}</p>
    </div>
  }

  <!-- No Results -->
  @if (!loading && filteredCompanies.length === 0) {
    <div class="card">
      <div class="card-body d-flex flex-column align-items-center py-20">
        <i class="fa fa-building fs-3x text-muted mb-5"></i>
        <h3 class="text-muted">{{ 'organisations.noResults' | translate }}</h3>
        <p class="text-muted">{{ 'organisations.noResultsHint' | translate }}</p>
      </div>
    </div>
  }

  <!-- Organisations Grid -->
  @if (!loading && filteredCompanies.length > 0) {
    <div class="row g-6 g-xl-9">
      @for (company of filteredCompanies; track company.company_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 organisation-card" [id]="'organisation-card-' + company.company_id">
            <!-- Header Image -->
            <div class="organisation-header">
              @if (company.header_image_url) {
                <img [src]="company.header_image_url" class="header-image" [alt]="company.company_name"/>
              } @else {
                <div class="header-placeholder">
                  <i class="fa fa-building"></i>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Type & Distance Badges -->
              <div class="d-flex flex-wrap gap-2 mb-3">
                @if (company.company_type) {
                  <span class="type-badge type-{{ company.company_type }}">
                    <i class="fa {{ getCompanyTypeIcon(company.company_type) }}"></i>
                    {{ getCompanyTypeLabel(company.company_type) }}
                  </span>
                }
                @if (userLocation && getCompanyDistance(company) !== null) {
                  <span class="badge badge-primary">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getCompanyDistance(company)) }} {{ 'organisations.away' | translate }}
                  </span>
                }
              </div>

              <!-- Logo + Name Row -->
              <div class="d-flex align-items-center mb-4">
                <div class="organisation-logo me-3">
                  @if (company.company_logo_url) {
                    <img [src]="company.company_logo_url" [alt]="company.company_name"/>
                  } @else {
                    <div class="logo-placeholder">
                      <span>{{ getCompanyInitials(company) }}</span>
                    </div>
                  }
                </div>
                <div>
                  <h3 class="card-title fw-bold fs-4 mb-1">{{ company.company_name }}</h3>
                  @if (company.city || company.country) {
                    <p class="text-muted mb-0 fs-7">
                      <i class="fa fa-location-dot me-1"></i>
                      {{ company.city }}{{ company.city && company.country ? ', ' : '' }}{{ company.country }}
                    </p>
                  }
                </div>
              </div>

              <!-- Description (HTML formatted) -->
              @if (company.description) {
                <div class="text-gray-600 mb-5 organisation-description" [innerHTML]="company.description"></div>
              }

              <!-- Contact Info -->
              <div class="mb-5 mt-auto">
                @if (company.email) {
                  <div class="d-flex align-items-center mb-2">
                    <i class="fa fa-envelope text-primary me-3 fs-6"></i>
                    <a [href]="'mailto:' + company.email" class="text-gray-700 text-hover-primary text-truncate">{{ company.email }}</a>
                  </div>
                }
                @if (company.phone) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-phone text-primary me-3 fs-6"></i>
                    <a [href]="'tel:' + company.phone" class="text-gray-700 text-hover-primary">{{ company.phone }}</a>
                  </div>
                }
              </div>

              <!-- Action Button -->
              <div class="mt-auto">
                <app-button
                  [variant]="'primary'"
                  [label]="'organisations.viewDetails' | translate"
                  [routerLink]="'/organisations/' + company.company_id"
                  [width]="'100%'"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
